import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircle } from "lucide-react";
import { useMemo } from "react";
import { useCreateQuotationInvoiceDocument } from "../api/queries";
import {
  buildDocumentCreateStockPrefill,
  parseQiCreatePrefillSearch,
} from "../lib/qi-create-prefill";
import {
  DocumentCreateTour,
  useDocumentCreateTour,
} from "../tour/DocumentCreateTour";
import { DocumentCreateForm } from "../ui/DocumentCreateForm";
import { DocumentTypeManagementModal } from "../ui/DocumentTypeManagementModal";

export function QuotationInvoiceDocumentCreatePage() {
  const nav = useNavigate({ from: "/app/documents/new" });
  const search = useSearch({ from: "/app/documents/new" });
  const stockPrefill = useMemo(
    () => buildDocumentCreateStockPrefill(parseQiCreatePrefillSearch(search)),
    [search],
  );
  const createMutation = useCreateQuotationInvoiceDocument();
  const tour = useDocumentCreateTour();
  const documentTypeModal = useDisclosure();
  const canUpdate = useActionPermission(["quotation_invoice:update"]);

  const handleSubmit = (payload: {
    documentType: string;
    status: "draft" | "issued";
    documentNumber: string;
    customerId: string | null;
    customerName: string;
    customerAddress: string | null;
    customerContact: string | null;
    issueDate: string | null;
    dueDate: string | null;
    referencePo: string | null;
    paymentTerms: string | null;
    notes: string | null;
    templateId: string | null;
    items: Array<{
      description: string;
      quantity: number;
      unit: string | null;
      unitPrice: number;
      discountAmount?: number;
      taxRate?: number;
      productId?: string | null;
      rawMaterialId?: string | null;
    }>;
    discountAmount: number;
    taxRate: number;
    depositPercent: number;
    currency: string;
    showSignature: boolean;
    stockBranchId?: string | null;
  }) => {
    createMutation.mutate(
      {
        documentType: payload.documentType,
        status: payload.status,
        documentNumber: payload.documentNumber || null,
        customerId: payload.customerId ?? null,
        customerName: payload.customerName,
        customerAddress: payload.customerAddress ?? null,
        customerContact: payload.customerContact ?? null,
        issueDate: payload.issueDate ?? null,
        dueDate: payload.dueDate ?? null,
        currency: payload.currency,
        paymentTerms: payload.paymentTerms ?? null,
        notes: payload.notes ?? null,
        templateId: payload.templateId ?? null,
        stockBranchId: payload.stockBranchId ?? null,
        items: payload.items,
        discountAmount: payload.discountAmount,
        taxRate: payload.taxRate,
        depositPercent: payload.depositPercent,
        showSignature: payload.showSignature,
      },
      {
        onSuccess: (data) => {
          nav({ to: "/app/documents/$id", params: { id: data.id } });
        },
      },
    );
  };

  return (
    <>
      <Header />
      <Main>
        <DocumentCreateTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="space-y-2">
          <div className="mb-4 flex items-center justify-between space-x-2">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">
                ສ້າງເອກະສານໃໝ່
              </h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                nav({
                  to: "/app/documents",
                  search: {
                    offset: 0,
                    limit: 20,
                    sort: undefined,
                    filters: undefined,
                  },
                })
              }
              className="gap-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບໄປລາຍການເອກະສານ
            </Button>
          </div>
        </div>
        <DocumentTypeManagementModal
          open={documentTypeModal.isOpen}
          onOpenChange={(open) =>
            open ? documentTypeModal.open() : documentTypeModal.close()
          }
        />

        <div data-tourid="document-create-form">
          <DocumentCreateForm
            onSubmit={handleSubmit}
            submitting={createMutation.isPending}
            stockPrefill={stockPrefill}
            onOpenDocumentTypeModal={
              canUpdate ? documentTypeModal.open : undefined
            }
          />
        </div>
      </Main>
    </>
  );
}
